import { Component, OnInit, Input, ViewChild, Optional, Self } from '@angular/core';
import { NgControl } from '@angular/forms';
import { FieldConventionService, ValidationMessageService } from 'src/app/stp/stp-app/forms';
import { MatSelect } from '@angular/material';
import { SelectItem } from 'src/app/stp/stp-app/forms/models/select-item';
import { Observable, of } from 'rxjs';
import { map, flatMap, distinct, toArray, delay, catchError, finalize } from 'rxjs/operators';

@Component({
  selector: 'stp-select',
  templateUrl: './select.component.html',
  styleUrls: ['./select.component.scss']
})
export class SelectComponent implements OnInit {

  @Input() label: string;
  @Input() required: boolean;
  @Input() hint?: string;
  @Input() errorMessage?: string;
  @Input() multiple = false;
  @Input() isGrouped = false;
  @Input() items: Observable<SelectItem[]>;

  @ViewChild(MatSelect) private matSelect: MatSelect;

  selectItems: Observable<SelectItem[]>;
  groupNames: Observable<string[] | any>;
  isErrorState = false;
  loading = false;

  constructor(
    @Optional() @Self() public ngControl: NgControl,
    private fieldConventionService: FieldConventionService,
    public validationMessageService: ValidationMessageService) {

      if (this.ngControl) {
        this.ngControl.valueAccessor = this;
      }
  }

  ngOnInit() {
    if (this.ngControl) {
      this.matSelect.ngControl = this.ngControl;
    }

    if (!this.label) {
      this.label = this.fieldConventionService.getDefaultLabel(this.ngControl.name);
    }

    this.selectItems = this.loadItems();

    if (this.isGrouped) {
      this.buildGroups();
    }
  }

  writeValue(obj: any): void {
    this.matSelect.value = obj;
  }

  registerOnChange(fn: any): void {
    this.matSelect.registerOnChange(fn);
  }

  registerOnTouched(fn: any): void {
    this.matSelect.registerOnTouched(fn);
  }

  setDisabledState?(isDisabled: boolean): void {
    this.matSelect.setDisabledState(isDisabled);
  }

  getItemsForGroup(groupName: string): Observable<SelectItem[]> {
    return this.selectItems.pipe(
      map(o => {
        return o.filter(l => {
          return l.groupName === groupName;
        });
      })
    );
  }

  getErrorMessage() {
    if (this.errorMessage) {
      return this.errorMessage;
    }

    return this.validationMessageService.getMessage(this.label, this.ngControl.errors);
  }

  filterOptions = (selectItems: SelectItem[]) => {
    return selectItems.filter(selectItem => !selectItem.archived || (this.ngControl.value === selectItem.id));
  }

  private loadItems() {
    this.loading = true;

    /**
     * `delay(0)` defaults to `async` scheduler, i.e., macrotask queue.
     * Fixes issue where lookup resolves synchronously from cache,
     * which modifies `loading` in `ngOnInit` and throws an
     * `ExpressionChangedAfterItHasBeenCheckedError`.
     */
    return this.items
      .pipe(
        delay(0),
        map(this.filterOptions),
        catchError((err) => {
          console.warn(`Error loading items for '${this.label}': showing no items`);
          this.setAsErrored();
          return of([]);
        }),
        finalize(() => {
          this.loading = false;
        })
      );
  }

  private buildGroups() {
    this.groupNames = this.selectItems
      .pipe(
        map(option => {
          return option.map(selectItem => selectItem.groupName);
        }),
        flatMap(name => name),
        distinct(),
        toArray()
      );
  }

  private setAsErrored() {
    this.isErrorState = true;
    this.setDisabledState(true);
  }
}
